import {
    BaseVariant,
    ConfigurableAttributeOption,
    ProductVariant,
} from 'src/components/ProductDetail/utils/product-detail.types';
import { CartAddConfigItem, CartAddSimpleParams } from 'src/core/state/cart';
import { Size } from 'src/components/ProductDetail/ProductSizes';

/**
 * A utility for selecting a variant based on selections
 *
 * @param selected
 * @param variants
 */
export function selectVariantFromOptions<T extends BaseVariant>(
    selected: { [index: string]: number },
    variants: T[],
): T | undefined {
    return variants.find((variant) => {
        return variant.attributes.every((attr) => {
            const { code, value_index } = attr;
            return Number(selected[code]) === Number(value_index);
        });
    });
}

export function selectSizetFromOptions<T extends BaseVariant>(size: number, variants: T[]): T | undefined {
    return variants.find((variant) => {
        const variantSize = variant.attributes[1];
        const { value_index } = variantSize;
        return size === value_index;
    });
}

type AtbMeta = { attributes: ConfigurableAttributeOption[]; qty: number; small_image: string };

/**
 * Convert form values into something that can be added to cart
 *
 * @param parentSku
 * @param values
 * @param variants
 */
export function formValuesToCartAddInput(
    parentSku: string,
    values: { [index: string]: any },
    variants: ProductVariant[],
): (CartAddConfigItem & AtbMeta)[] {
    const color: number = values.color;
    if (!color) {
        throw new Error("Form was submitted without a 'color' selection");
    }
    return Object.keys(values)
        .filter((x) => x.startsWith('size:'))
        .map((key) => {
            const [, size] = key.split(':');
            const qty = parseInt(values[key]);
            if (qty === 0) return undefined;
            const options = {
                color,
                size: parseInt(size),
            };
            const variant = selectVariantFromOptions(options, variants);
            if (!variant) return undefined;
            return {
                data: {
                    quantity: qty,
                    sku: variant.sku,
                },
                parent_sku: parentSku,
                variant_sku: variant.sku,
                attributes: variant.attributes,
                small_image: variant.small_image,
                qty,
            };
        })
        .filter(Boolean) as (CartAddConfigItem & AtbMeta)[];
}

/**
 * Convert a parent sku + qty into add-to-cart for simple products
 *
 * @param parentSku
 * @param qty
 */
export function formValuesToCartAddSimpleInput(parentSku: string, qty = 1): CartAddSimpleParams {
    return {
        items: [
            {
                data: {
                    quantity: qty,
                    sku: parentSku,
                },
            },
        ],
    };
}

/**
 * Try to convert form values into an overall quantity
 *
 * @param values
 */
export function formValuesToQty(values: { [index: string]: any }): number {
    const keys = Object.keys(values);
    if (keys.length === 0) {
        return 0;
    }
    return keys
        .filter((x) => x.indexOf('size:') === 0)
        .reduce((acc, key) => {
            const curr = values[key];
            const int = parseInt(curr);
            return acc + int;
        }, 0);
}

export function appendStockToSize<T extends BaseVariant>(
    size: Size,
    selections: Record<string, number | undefined>,
    variants: (T & { product?: { stock_status: number; stock_qty: number; id: number; sku: string } })[],
): Size {
    const fake_selection = {
        ...selections,
        size: Number(size.id),
    };
    const variant = selectVariantFromOptions(fake_selection as { [index: string]: number }, variants!);
    if (!variant) return size;
    return {
        ...size,
        stock: variant.stock_status ?? variant.product?.stock_status,
        maxQty: variant.max_qty ?? variant.product?.stock_qty ?? null,
        simpleProductId: variant.id ?? variant.product?.id ?? null,
        simpleProductSku: variant.sku ?? variant.product?.sku ?? null,
    };
}
